import { useCallback, useRef, useState } from "react";
import { API_BASE, authFetch } from "@/lib/api";

/** Speaks a reply while it is still streaming. Text is pushed in sentence-sized
 * chunks; each chunk's audio is fetched as soon as it arrives (one chunk
 * pre-fetched ahead) and played in order, so speech starts about a second after
 * the first sentence appears instead of after the whole answer finishes. */
export function useSpeechQueue() {
    const [speaking, setSpeaking] = useState(false);
    const queue = useRef([]);          // pending texts
    const pending = useRef([]);        // in-flight/ready object URLs, in order
    const audioRef = useRef(null);
    const runningRef = useRef(false);
    const cancelledRef = useRef(false);

    const fetchClip = useCallback(async (text) => {
        const res = await authFetch(`${API_BASE}/utsav/speak`, ({
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text }),
        }));
        if (!res.ok) throw new Error("tts failed");
        return URL.createObjectURL(await res.blob());
    }, []);

    const drain = useCallback(async () => {
        if (runningRef.current) return;
        runningRef.current = true;
        setSpeaking(true);
        try {
            while (!cancelledRef.current && (queue.current.length || pending.current.length)) {
                while (pending.current.length < 2 && queue.current.length) {
                    pending.current.push(fetchClip(queue.current.shift()).catch(() => null));
                }
                const url = await pending.current.shift();
                if (cancelledRef.current) break;
                if (!url) continue;
                await new Promise((resolve) => {
                    const audio = new Audio(url);
                    audioRef.current = audio;
                    audio.onended = audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    audio.play().catch(() => resolve());
                });
            }
        } finally {
            runningRef.current = false;
            if (!queue.current.length && !pending.current.length) setSpeaking(false);
        }
    }, [fetchClip]);

    const push = useCallback((text) => {
        const clean = (text || "").trim();
        if (!clean) return;
        cancelledRef.current = false;
        queue.current.push(clean);
        drain();
    }, [drain]);

    const stop = useCallback(() => {
        cancelledRef.current = true;
        queue.current = [];
        pending.current = [];
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        setSpeaking(false);
    }, []);

    return { speaking, push, stop };
}

/** Splits streamed text into speakable chunks at sentence boundaries — including
 * the Devanagari/Bengali danda — returning [chunks, leftover]. */
export function takeSpeakableChunks(buffer, { minLength = 40, maxLength = 320 } = {}) {
    const chunks = [];
    let rest = buffer;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const match = rest.match(/[.!?।॥:;]\s|\n+/);
        let cut = match ? match.index + match[0].length : -1;
        if (cut < 0 && rest.length > maxLength) {
            const space = rest.lastIndexOf(" ", maxLength);
            cut = space > minLength ? space + 1 : maxLength;
        }
        if (cut < 0) break;
        const candidate = rest.slice(0, cut);
        if (candidate.trim().length < minLength && rest.length < maxLength) break;
        chunks.push(candidate.trim());
        rest = rest.slice(cut);
    }
    return [chunks, rest];
}
